import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { 
  Trash2, AlertTriangle, Database, RefreshCw, Users, FileText, 
  Calendar, ClipboardList, Receipt, Upload, Shield, CheckCircle2,
  Zap, Home
} from 'lucide-react';
import { toast } from 'sonner';

export default function DataManagementPage() {
  const { api, user, isDirector } = useAuth();
  const [loading, setLoading] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [counts, setCounts] = useState(null);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [showClearAllDialog, setShowClearAllDialog] = useState(false);
  const [clearOptions, setClearOptions] = useState({
    clear_assessments: false,
    clear_evaluations: false,
    clear_progress_notes: false,
    clear_invoices: false,
    clear_appointments: false,
    clear_patients: false,
    clear_families: false,
    clear_uploads: false
  });
  const [clearResults, setClearResults] = useState(null);
  
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  
  useEffect(() => {
    if (isDirector || isSuperAdmin) {
      fetchDataCounts();
    }
  }, [isDirector, isSuperAdmin]);
  
  const fetchDataCounts = async () => {
    try {
      const res = await api.get('/data-management/data-counts');
      setCounts(res.data);
    } catch (error) {
      console.error('Failed to fetch data counts:', error);
    }
  };
  
  // Clear ALL test data at once
  const handleClearAllData = async () => {
    setClearingAll(true);
    try {
      const res = await api.post('/data-management/clear-all-test-data');
      setClearResults(res.data);
      toast.success('All test data cleared successfully!');
      fetchDataCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear data');
    } finally {
      setClearingAll(false);
      setShowClearAllDialog(false);
    }
  };
  
  const handleClearData = async () => {
    // Validate at least one option is selected
    const hasSelection = Object.values(clearOptions).some(v => v);
    if (!hasSelection) {
      toast.error('Please select at least one data type to clear');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/data-management/clear-test-data', clearOptions);
      setClearResults(res.data);
      toast.success('Data cleared successfully');
      fetchDataCounts();
      
      // Reset options
      setClearOptions({
        clear_assessments: false,
        clear_evaluations: false,
        clear_progress_notes: false,
        clear_invoices: false,
        clear_appointments: false,
        clear_patients: false,
        clear_families: false,
        clear_uploads: false
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear data');
    } finally {
      setLoading(false);
      setShowClearDialog(false);
    }
  };
  
  const toggleOption = (key) => {
    setClearOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  const dataTypes = [
    { key: 'clear_patients', label: 'Patients/Children', icon: Users, count: counts?.patients, color: 'text-blue-600' },
    { key: 'clear_families', label: 'Families', icon: Home, count: counts?.families, color: 'text-indigo-600' },
    { key: 'clear_assessments', label: 'Assessments', icon: ClipboardList, count: counts?.assessments, color: 'text-purple-600' },
    { key: 'clear_evaluations', label: 'Evaluations', icon: FileText, count: counts?.evaluations, color: 'text-pink-600' },
    { key: 'clear_progress_notes', label: 'Progress Notes', icon: FileText, count: counts?.progress_notes, color: 'text-teal-600' },
    { key: 'clear_invoices', label: 'Invoices', icon: Receipt, count: counts?.invoices, color: 'text-amber-600' },
    { key: 'clear_appointments', label: 'Appointments', icon: Calendar, count: counts?.appointments, color: 'text-green-600' },
    { key: 'clear_uploads', label: 'Uploads/Attachments', icon: Upload, count: counts?.uploads, color: 'text-slate-600' }
  ];
  
  // Calculate total records
  const totalRecords = counts ? 
    (counts.patients || 0) + (counts.families || 0) + (counts.assessments || 0) + 
    (counts.evaluations || 0) + (counts.progress_notes || 0) + (counts.invoices || 0) + 
    (counts.appointments || 0) : 0;
  
  if (!isDirector && !isSuperAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-bold text-slate-700">Access Restricted</h2>
          <p className="text-slate-500">Only Directors can access data management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="data-management-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">Data Management</h1>
          <p className="text-slate-500 mt-1">Clear test data and manage system records</p>
        </div>
        
        {/* Quick Clear All Button */}
        <Button 
          variant="destructive" 
          size="lg"
          onClick={() => setShowClearAllDialog(true)}
          disabled={totalRecords === 0}
          className="bg-red-600 hover:bg-red-700"
          data-testid="btn-clear-all"
        >
          <Zap className="w-5 h-5 mr-2" />
          Clear All Test Data ({totalRecords})
        </Button>
      </div>
      
      {/* Warning Alert */}
      <Alert className="bg-amber-50 border-amber-200">
        <AlertTriangle className="w-5 h-5 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <strong>Warning:</strong> Clearing data is permanent and cannot be undone. 
          Only clear data for testing purposes. This will affect all users.
        </AlertDescription>
      </Alert>
      
      {/* Data Counts Summary */}
      <Card className="card-icrcl">
        <CardHeader>
          <CardTitle className="text-lg text-[#1e3a5f] flex items-center gap-2">
            <Database className="w-5 h-5" />
            Current Data Summary
          </CardTitle>
          <CardDescription>Overview of all data in the system</CardDescription>
        </CardHeader>
        <CardContent>
          {counts ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <Users className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                <p className="text-2xl font-bold text-[#1e3a5f]">{counts.patients}</p>
                <p className="text-sm text-slate-500">Patients</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <Home className="w-8 h-8 mx-auto mb-2 text-indigo-600" />
                <p className="text-2xl font-bold text-[#1e3a5f]">{counts.families}</p>
                <p className="text-sm text-slate-500">Families</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <ClipboardList className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                <p className="text-2xl font-bold text-[#1e3a5f]">{counts.assessments}</p>
                <p className="text-sm text-slate-500">Assessments</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <Receipt className="w-8 h-8 mx-auto mb-2 text-amber-600" />
                <p className="text-2xl font-bold text-[#1e3a5f]">{counts.invoices}</p>
                <p className="text-sm text-slate-500">Invoices</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400" />
              <p className="text-slate-500 mt-2">Loading data counts...</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Clear Data Options */}
      <Card className="card-icrcl border-red-200">
        <CardHeader>
          <CardTitle className="text-lg text-red-700 flex items-center gap-2">
            <Trash2 className="w-5 h-5" />
            Clear Test Data (Selective)
          </CardTitle>
          <CardDescription>Select specific data types you want to clear</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dataTypes.map(({ key, label, icon: Icon, count, color }) => (
              <div 
                key={key}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  clearOptions[key] 
                    ? 'bg-red-50 border-red-300' 
                    : 'bg-slate-50 border-slate-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${color}`} />
                  <div>
                    <p className="font-medium text-slate-800">{label}</p>
                    <p className="text-sm text-slate-500">{count || 0} records</p>
                  </div>
                </div>
                <Switch 
                  checked={clearOptions[key]}
                  onCheckedChange={() => toggleOption(key)}
                  data-testid={`toggle-${key}`}
                />
              </div>
            ))}
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setClearOptions({
                clear_assessments: false,
                clear_evaluations: false,
                clear_progress_notes: false,
                clear_invoices: false,
                clear_appointments: false,
                clear_patients: false,
                clear_families: false,
                clear_uploads: false
              })}
            >
              Reset Selection
            </Button>
            <Button 
              variant="destructive"
              onClick={() => setShowClearDialog(true)}
              disabled={!Object.values(clearOptions).some(v => v)}
              data-testid="btn-clear-selected"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Selected Data
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Clear Results */}
      {clearResults && clearResults.cleared?.length > 0 && (
        <Card className="card-icrcl bg-green-50 border-green-200">
          <CardHeader>
            <CardTitle className="text-lg text-green-700 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Data Cleared Successfully
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {clearResults.cleared?.map((item, idx) => (
                <li key={idx} className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="w-4 h-4" />
                  {item}
                </li>
              ))}
            </ul>
            {clearResults.errors?.length > 0 && (
              <div className="mt-4">
                <p className="font-medium text-red-600">Errors:</p>
                <ul className="text-red-600">
                  {clearResults.errors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Selective Clear Confirmation Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Confirm Data Deletion
            </DialogTitle>
            <DialogDescription>
              This action is permanent and cannot be undone!
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-slate-700 mb-4">You are about to delete the following data:</p>
            <div className="space-y-2">
              {dataTypes.filter(d => clearOptions[d.key]).map(({ key, label, count }) => (
                <div key={key} className="flex items-center gap-2 p-2 rounded bg-red-100 text-red-700">
                  <Badge variant="destructive">{count || 0}</Badge>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleClearData}
              disabled={loading}
              data-testid="btn-confirm-clear"
            >
              {loading ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Clearing...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Yes, Delete Data</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Clear ALL Confirmation Dialog */}
      <Dialog open={showClearAllDialog} onOpenChange={setShowClearAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Clear ALL Test Data?
            </DialogTitle>
            <DialogDescription>
              This will delete ALL patients, families, assessments, invoices, appointments, and other clinical/financial data. This action CANNOT be undone!
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 bg-red-50 rounded-lg p-4">
            <p className="text-red-800 font-medium mb-3">The following will be permanently deleted:</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between">
                <span>Patients:</span>
                <Badge variant="destructive">{counts?.patients || 0}</Badge>
              </div>
              <div className="flex justify-between">
                <span>Families:</span>
                <Badge variant="destructive">{counts?.families || 0}</Badge>
              </div>
              <div className="flex justify-between">
                <span>Assessments:</span>
                <Badge variant="destructive">{counts?.assessments || 0}</Badge>
              </div>
              <div className="flex justify-between">
                <span>Progress Notes:</span>
                <Badge variant="destructive">{counts?.progress_notes || 0}</Badge>
              </div>
              <div className="flex justify-between">
                <span>Invoices:</span>
                <Badge variant="destructive">{counts?.invoices || 0}</Badge>
              </div>
              <div className="flex justify-between">
                <span>Appointments:</span>
                <Badge variant="destructive">{counts?.appointments || 0}</Badge>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-red-200 flex justify-between font-bold">
              <span>Total Records:</span>
              <Badge variant="destructive" className="text-lg px-3">{totalRecords}</Badge>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearAllDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleClearAllData}
              disabled={clearingAll}
              className="bg-red-600 hover:bg-red-700"
              data-testid="btn-confirm-clear-all"
            >
              {clearingAll ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Clearing All...</>
              ) : (
                <><Zap className="w-4 h-4 mr-2" /> Yes, Clear Everything</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
